import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ContactService, ContactPayload } from '../../../services/contact.service';
import { SettingService, SiteSetting } from '../../../services/setting.service';

/** Génère un entier aléatoire [min, max] */
function rand(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslateModule, TranslatePipe],
  templateUrl: './contact.html',
  styleUrl: './contact.css',
})
export class ContactComponent implements OnInit {

  /* ── Paramètres du site ────────────────────────────────────────── */
  settings: SiteSetting | null = null;

  /* ── Formulaire ────────────────────────────────────────────────── */
  form = {
    name:    '',
    email:   '',
    phone:   '',
    subject: '',
    message: '',
    captcha: '',
  };

  touched: Record<string, boolean> = {};
  sending   = false;
  submitted = false;
  error     = '';

  /* ── Captcha mathématique ──────────────────────────────────────── */
  captchaA = 0;
  captchaB = 0;

  /* ── Google Maps ──────────────────────────────────────────────── */
  mapUrl: SafeResourceUrl = '';

  constructor(
    private contactService: ContactService,
    private settingService: SettingService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.newCaptcha();

    const lang = (this.translate.currentLang as any) || 'fr';
    this.settingService.getSettings(lang).subscribe({
      next: s => {
        this.settings = s;
        // adresse est maintenant un objet multilingue → on prend la version résolue
        this.buildMapUrl(s.adresse_text ?? this.pickLang(s.adresse));
      },
      error: () => { /* settings optionnels */ },
    });
  }

  /* ── Captcha ──────────────────────────────────────────────────── */
  newCaptcha(): void {
    this.captchaA = rand(1, 12);
    this.captchaB = rand(1, 12);
    this.form.captcha = '';
  }

  get captchaExpected(): number {
    return this.captchaA + this.captchaB;
  }

  get captchaValid(): boolean {
    return +this.form.captcha === this.captchaExpected;
  }

  /* ── Map ──────────────────────────────────────────────────────── */
  private buildMapUrl(address: string | null | undefined): void {
    const query = encodeURIComponent(address ?? 'Mauritanie');
    const raw = `https://maps.google.com/maps?q=${query}&hl=fr&output=embed`;
    this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);
  }

  /** Fallback : lang courante → fr → première valeur non vide. */
  pickLang(obj: any): string {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    const lang = (this.translate.currentLang as any) || 'fr';
    return obj[lang] || obj.fr || Object.values(obj).find((v: any) => !!v) as string || '';
  }

  /* ── Validation ───────────────────────────────────────────────── */
  touch(field: string): void {
    this.touched[field] = true;
  }

  isInvalid(field: string): boolean {
    return !!this.touched[field] && !this.getValue(field);
  }

  isEmailInvalid(): boolean {
    return !!this.touched['email'] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email);
  }

  isCaptchaInvalid(): boolean {
    return !!this.touched['captcha'] && !this.captchaValid;
  }

  private getValue(field: string): string {
    return (this.form as any)[field]?.trim() ?? '';
  }

  get formIsValid(): boolean {
    return !!(
      this.form.name.trim() &&
      this.form.email.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email) &&
      this.form.subject.trim() &&
      this.form.message.trim() &&
      this.captchaValid
    );
  }

  /* ── Soumission ───────────────────────────────────────────────── */
  onSubmit(): void {
    // Marquer tous les champs comme touchés pour afficher les erreurs
    ['name', 'email', 'subject', 'message', 'captcha'].forEach(f => (this.touched[f] = true));

    if (!this.formIsValid) return;

    this.sending = true;
    this.error   = '';

    const payload: ContactPayload = {
      name:    this.form.name.trim(),
      email:   this.form.email.trim(),
      phone:   this.form.phone.trim() || undefined,
      subject: this.form.subject.trim(),
      message: this.form.message.trim(),
    };

    this.contactService.send(payload).subscribe({
      next: () => {
        this.submitted = true;
        this.sending   = false;
      },
      error: (err) => {
        this.error   = err?.error?.message ?? this.translate.instant('contact_erreur_reseau');
        this.sending  = false;
      },
    });
  }

  reset(): void {
    this.form      = { name: '', email: '', phone: '', subject: '', message: '', captcha: '' };
    this.touched   = {};
    this.submitted = false;
    this.error     = '';
    this.newCaptcha();
  }

  /* ── Réseaux sociaux ─────────────────────────────────────────── */
  get hasSocials(): boolean {
    return !!(
      this.settings?.facebook ||
      this.settings?.twitter  ||
      this.settings?.linkedin ||
      this.settings?.instagram
    );
  }

  /* ── Langue active ───────────────────────────────────────────── */
  get isRtl(): boolean {
    return this.translate.currentLang === 'ar';
  }
}
